import React, { useState, useEffect, FormEvent } from 'react';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
// import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaUser, FaEnvelope, FaBed, FaCalendarAlt, FaCalculator, FaCheckCircle, FaPercent } from 'react-icons/fa';

interface Service {
    id: string;
    name: string;
    description: string;
    price: number;
    originalPrice?: number;
}

const AppointmentForm: React.FC = () => {
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [checkInDate, setCheckInDate] = useState('');
    const [checkOutDate, setCheckOutDate] = useState('');
    const [roomType, setRoomType] = useState('');
    const [guestCount, setGuestCount] = useState(1);
    const [specialRequests, setSpecialRequests] = useState('');
    const [services, setServices] = useState<Service[]>([]);
    const [confirmationMessage, setConfirmationMessage] = useState<string>('');
    const [servicePrice, setServicePrice] = useState<number>(0);
    const [totalCost, setTotalCost] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [serviceOriginalPrice, setServiceOriginalPrice] = useState<number>(0);
    const [applyFamilyDiscount, setApplyFamilyDiscount] = useState(false);
    const [availabilityMessage, setAvailabilityMessage] = useState<string>('');
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

    // Room inventory
    const ROOM_INVENTORY = {
        'Suite Room': 2,
        'Single Room': 4
    };

    // Payment gateway hooks - commented out for now
    // const stripe = useStripe();
    // const elements = useElements();

    // Predefined room types with discounted pricing
    const roomTypes = [
        { 
            name: 'Suite Room', 
            originalPrice: 6000, 
            price: 6000,
            description: 'Luxurious suite with premium amenities' 
        },
        { 
            name: 'Single Room', 
            originalPrice: 2500, 
            price: 2500,
            description: 'Comfortable room with essential amenities' 
        }
    ];

    useEffect(() => {
        // Fetch room types from Firebase instead of using hardcoded values
        const fetchRoomTypes = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'roomTypes'));
                const roomTypesData = querySnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        name: data.name,
                        description: data.description,
                        price: data.discountedPrice, // Use discounted price
                        originalPrice: data.originalPrice,
                        isActive: data.isActive
                    };
                });
                
                // Filter only active room types
                const activeRoomTypes = roomTypesData.filter(room => room.isActive !== false);
                setServices(activeRoomTypes);
            } catch (error) {
                console.error('Error fetching room types:', error);
                // Fallback to hardcoded values if Firebase fails
                setServices(roomTypes.map((room, index) => ({
                    id: index.toString(),
                    name: room.name,
                    description: room.description,
                    price: room.price,
                    originalPrice: room.originalPrice
                })));
            }
        };

        fetchRoomTypes();
    }, []);

    useEffect(() => {
        const selectedService = services.find(s => s.name === roomType);
        setServicePrice(selectedService ? selectedService.price : 0);
        setServiceOriginalPrice(selectedService ? (selectedService.originalPrice || selectedService.price) : 0);
        
        // Remove family discount logic - discount is already applied to base prices
        setApplyFamilyDiscount(false);
    }, [roomType, services, guestCount]);

    useEffect(() => {
        if (checkInDate && checkOutDate && servicePrice) {
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            const timeDiff = checkOut.getTime() - checkIn.getTime();
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
            
            // Calculate total cost based on number of nights
            const cost = daysDiff * servicePrice;
            setTotalCost(cost);
        } else {
            setTotalCost(0);
        }
    }, [checkInDate, checkOutDate, servicePrice]);

    const getDaysDiff = () => {
        if (checkInDate && checkOutDate) {
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            const timeDiff = checkOut.getTime() - checkIn.getTime();
            return Math.ceil(timeDiff / (1000 * 3600 * 24));
        }
        return 0;
    };

    // Check room availability for selected dates
    const checkRoomAvailability = async (selectedRoomType: string, checkIn: string, checkOut: string) => {
        if (!selectedRoomType || !checkIn || !checkOut) return true;

        try {
            setIsCheckingAvailability(true);
            const bookingsSnapshot = await getDocs(collection(db, 'bookings'));
            
            const checkInDate = new Date(checkIn);
            const checkOutDate = new Date(checkOut);
            
            // Count existing bookings for this room type that overlap with selected dates
            let bookedRooms = 0;
            
            bookingsSnapshot.forEach((doc) => {
                const booking = doc.data();
                if (booking.roomType === selectedRoomType) {
                    const existingCheckIn = new Date(booking.checkInDate);
                    const existingCheckOut = new Date(booking.checkOutDate);
                    
                    // Check if dates overlap
                    if (checkInDate < existingCheckOut && checkOutDate > existingCheckIn) {
                        bookedRooms++;
                    }
                }
            });
            
            const maxRooms = ROOM_INVENTORY[selectedRoomType as keyof typeof ROOM_INVENTORY] || 0;
            const availableRooms = maxRooms - bookedRooms;
            
            if (availableRooms <= 0) {
                setAvailabilityMessage(`❌ Sorry, no ${selectedRoomType}s available for selected dates. Please choose different dates.`);
                return false;
            } else {
                setAvailabilityMessage(`✅ Great! ${availableRooms} ${selectedRoomType}${availableRooms > 1 ? 's' : ''} available for your dates.`);
                return true;
            }
        } catch (error) {
            console.error('Error checking availability:', error);
            setAvailabilityMessage('⚠️ Unable to check availability. Please try again.');
            return true; // Allow booking if check fails
        } finally {
            setIsCheckingAvailability(false);
        }
    };

    // Check availability when room type or dates change
    useEffect(() => {
        if (roomType && checkInDate && checkOutDate) {
            checkRoomAvailability(roomType, checkInDate, checkOutDate);
        } else {
            setAvailabilityMessage('');
        }
    }, [roomType, checkInDate, checkOutDate]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        // Check availability before booking
        const isAvailable = await checkRoomAvailability(roomType, checkInDate, checkOutDate);
        if (!isAvailable) {
            setIsLoading(false);
            return;
        }

        const booking = {
            customerName,
            customerEmail,
            customerPhone,
            checkInDate,
            checkOutDate,
            roomType,
            guestCount,
            specialRequests,
            totalCost,
            familyDiscount: applyFamilyDiscount,
            paymentStatus: 'pending', // Will be paid on arrival
            createdAt: new Date().toISOString()
        };

        try {
            // Save booking to Firebase without payment processing
            await addDoc(collection(db, 'bookings'), booking);
            
            setConfirmationMessage('🎉 Booking Confirmed! Your reservation has been successfully processed. Payment will be collected during check-in. You will receive a confirmation email shortly.');
            
            // Scroll to top to show confirmation message
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            // Clear form
            setCustomerName('');
            setCustomerEmail('');
            setCustomerPhone('');
            setCheckInDate('');
            setCheckOutDate('');
            setRoomType('');
            setGuestCount(1);
            setSpecialRequests('');

            // Send confirmation email (without payment details)
            await fetch('/api/sendEmail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    customerEmail,
                    customerName,
                    checkInDate,
                    checkOutDate,
                    roomType,
                    totalCost,
                    guestCount,
                    familyDiscount: applyFamilyDiscount,
                    paymentStatus: 'Pay on arrival'
                }),
            });

        } catch (error) {
            console.error('Failed to process booking', error);
            setConfirmationMessage('Sorry, there was an error processing your booking. Please try again or contact us directly.');
            // Scroll to top to show error message
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsLoading(false);
        }

        /* PAYMENT GATEWAY CODE - COMMENTED OUT FOR NOW, WILL ENABLE NEXT WEEK
        try {
            const depositAmount = calculateDeposit();

            // Create a payment intent
            const response = await fetch('/api/create-payment-intent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ amount: Math.round(depositAmount * 100) }),
            });
            const { clientSecret } = await response.json();

            // Confirm the payment
            const cardElement = elements!.getElement(CardElement);
            const { error, paymentIntent } = await stripe!.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement!,
                    billing_details: {
                        name: customerName,
                        email: customerEmail,
                        phone: customerPhone,
                    },
                },
            });

            if (error) {
                console.error('Payment error:', error);
                setIsLoading(false);
                return;
            }

            if (paymentIntent!.status === 'succeeded') {
                await addDoc(collection(db, 'bookings'), booking);
                
                setConfirmationMessage('🎉 Booking Confirmed! Your reservation has been successfully processed. You will receive a confirmation email shortly.');
                
                // Clear form
                setCustomerName('');
                setCustomerEmail('');
                setCustomerPhone('');
                setCheckInDate('');
                setCheckOutDate('');
                setRoomType('');
                setGuestCount(1);
                setSpecialRequests('');

                if (cardElement) {
                    cardElement.clear();
                }

                // Send confirmation email
                await fetch('/api/sendEmail', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        customerEmail,
                        customerName,
                        checkInDate,
                        checkOutDate,
                        roomType,
                        totalCost,
                        guestCount,
                        familyDiscount: applyFamilyDiscount
                    }),
                });
            }
        } catch (error) {
            console.error('Failed to process booking', error);
        } finally {
            setIsLoading(false);
        }
        */
    };

    const isFormValid = () => {
        const basicValidation = customerName && customerEmail && customerPhone && checkInDate && checkOutDate && roomType && !isLoading;
        const availabilityValid = !availabilityMessage || availabilityMessage.includes('✅');
        return basicValidation && availabilityValid && !isCheckingAvailability;
    };

    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const getCheckOutMinDate = () => {
        if (checkInDate) {
            const checkIn = new Date(checkInDate);
            checkIn.setDate(checkIn.getDate() + 1); // Next day after check-in
            return checkIn.toISOString().split('T')[0];
        }
        return getTomorrowDate();
    };

    return (
        <div className="max-w-4xl mx-auto">
            {confirmationMessage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 ease-out scale-100 opacity-100">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-t-2xl text-center">
                            <FaCheckCircle className="text-4xl mx-auto mb-3" />
                            <h3 className="text-2xl font-bold">Booking Confirmed!</h3>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-700 text-center leading-relaxed mb-6">
                                {confirmationMessage.replace('🎉 Booking Confirmed! ', '')}
                            </p>
                            <button
                                onClick={() => setConfirmationMessage('')}
                                className="w-full bg-primary hover:bg-primary-dark text-white py-3 px-6 rounded-full font-semibold transition-colors duration-200"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-text mb-6 flex items-center">
                        <FaUser className="mr-3 text-primary" />
                        Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                            <input
                                type="text"
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                                required
                                placeholder="Enter your full name"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                            <input
                                type="email"
                                value={customerEmail}
                                onChange={(e) => setCustomerEmail(e.target.value)}
                                required
                                placeholder="your.email@example.com"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                            <input
                                type="tel"
                                value={customerPhone}
                                onChange={(e) => setCustomerPhone(e.target.value)}
                                required
                                placeholder="+91 9121561555"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Guests *</label>
                            <select
                                value={guestCount}
                                onChange={(e) => setGuestCount(parseInt(e.target.value))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                            >
                                {[1,2,3,4,5,6,7,8].map(num => (
                                    <option key={num} value={num}>{num} Guest{num > 1 ? 's' : ''}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Booking Details */}
                <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-text mb-6 flex items-center">
                        <FaBed className="mr-3 text-primary" />
                        Booking Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Room Type *</label>
                            <select
                                value={roomType}
                                onChange={(e) => setRoomType(e.target.value)}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                            >
                                <option value="">Select a room type</option>
                                {services.map(service => (
                                    <option key={service.id} value={service.name}>
                                        {service.name} - ₹{service.price.toLocaleString()}/night
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Room Availability Status */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Check-in Date *</label>
                            <input
                                type="date"
                                value={checkInDate}
                                onChange={(e) => setCheckInDate(e.target.value)}
                                min={getTodayDate()}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Check-out Date *</label>
                            <input
                                type="date"
                                value={checkOutDate}
                                onChange={(e) => setCheckOutDate(e.target.value)}
                                min={getCheckOutMinDate()}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                            />
                        </div>
                        
                        {/* Room Availability Status */}
                        {(roomType && checkInDate && checkOutDate) && (
                            <div className="md:col-span-2">
                                {isCheckingAvailability ? (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                                        <span className="text-blue-700 font-medium">Checking room availability...</span>
                                    </div>
                                ) : availabilityMessage && (
                                    <div className={`border rounded-lg p-4 ${
                                        availabilityMessage.includes('✅') 
                                            ? 'bg-green-50 border-green-200 text-green-700' 
                                            : availabilityMessage.includes('❌')
                                            ? 'bg-red-50 border-red-200 text-red-700'
                                            : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                                    }`}>
                                        <span className="font-medium">{availabilityMessage}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Special Requests (Optional)</label>
                            <textarea
                                value={specialRequests}
                                onChange={(e) => setSpecialRequests(e.target.value)}
                                placeholder="Any special requirements or requests..."
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                            />
                        </div>
                    </div>
                </div>

                {/* Booking Summary */}
                {roomType && checkInDate && checkOutDate && (
                    <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl p-6">
                        <h3 className="text-xl font-bold text-text mb-6 flex items-center">
                            <FaCalculator className="mr-3 text-primary" />
                            Booking Summary
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Room Type:</span>
                                    <span className="font-semibold">{roomType}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Duration:</span>
                                    <span className="font-semibold">{getDaysDiff()} night{getDaysDiff() > 1 ? 's' : ''}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Rate per night:</span>
                                    <span className="font-semibold text-primary">₹{servicePrice.toLocaleString()}</span>
                                </div>

                            </div>
                            <div className="space-y-3">
                                <div className="flex justify-between text-lg">
                                    <span className="font-semibold">Total Cost:</span>
                                    <span className="font-bold text-primary">₹{totalCost.toLocaleString()}</span>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-green-800 font-semibold text-sm">💰 Payment on Arrival</p>
                                            <p className="text-green-700 text-sm mt-1">Full payment of ₹{totalCost.toLocaleString()} will be collected during check-in</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payment Section - Commented out for now */}
                {/* 
                <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-text mb-6 flex items-center">
                        <FaCreditCard className="mr-3 text-primary" />
                        Secure Payment
                    </h3>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <CardElement 
                            options={{
                                style: {
                                    base: {
                                        fontSize: '16px',
                                        color: '#424770',
                                        '::placeholder': {
                                            color: '#aab7c4',
                                        },
                                    },
                                },
                            }}
                        />
                    </div>
                    <p className="text-sm text-gray-600 mt-3 flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2" />
                        Your payment is secured with SSL encryption
                    </p>
                </div>
                */}

                {/* Submit Button */}
                <div className="text-center">
                    <button
                        type="submit"
                        disabled={!isFormValid()}
                        className={`w-full md:w-auto px-12 py-4 rounded-full font-bold text-lg transition-all duration-300 ${
                            isFormValid() 
                                ? 'bg-primary hover:bg-primary-dark text-white shadow-medium hover:shadow-large transform hover:-translate-y-1' 
                                : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                        }`}
                    >
                        {isLoading ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                            </span>
                        ) : (
                            'Confirm Booking - Pay on Arrival'
                        )}
                    </button>
                    <p className="text-sm text-gray-600 mt-4">
                        By booking, you agree to our terms and conditions. Payment will be collected during check-in.
                    </p>
                </div>
            </form>
        </div>
    );
};

export default AppointmentForm;